import { useCallback, useEffect, useRef, useState } from "react";
import BarcodeScanModal from "../components/BarcodeScanModal";
import { useOverlayHistory } from "../lib/useOverlayHistory";
import { SessionExpired, leaveSession } from "../lib/captureClient";
import {
  fetchLocations,
  fetchItems,
  fetchItem,
  setCount,
  messageFor,
} from "../lib/countClient";

// The stock count — walk the shelves, set what is actually there.
//
// ---------------------------------------------------------------------------
// THIS SCREEN WRITES TO A LIVE STOREFRONT
// ---------------------------------------------------------------------------
// The gallery next door fills a pile that something drains later, and its worst
// failure is a wasted walk. Every number confirmed here goes straight into the
// merchant's Shopify inventory, with no compare-and-set, on a box with no RDS
// backups. Three things follow from that and none of them are decoration:
//
//   THE LOCATION IS CHOSEN FIRST AND STAYS ON SCREEN. A correct count written
//   to the wrong location has no error message and no symptom. Nothing can be
//   counted until one is picked, and it is visible in the header for the whole
//   session so a person cannot forget which one they are in.
//
//   THE POPUP READS LIVE, ALWAYS. The list's number is what our sync last
//   mirrored and is frequently null or stale; opening an item asks Shopify.
//   The counter edits the live figure, not our copy of it.
//
//   NOTHING IS OPTIMISTIC. A count is not "saved" on screen until the backend
//   says Shopify took it. Showing a tick before the round trip returns would be
//   inventing the one fact this screen exists to establish.
//
// It reuses BarcodeScanModal rather than the capture camera: this half needs a
// barcode and nothing else, and the photography component stays untouched.

const CSS = `
html,body{margin:0; padding:0; background:#0D1312;}
.ct-root{
  min-height:100dvh; background:#0D1312; color:#E7EEEB;
  font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;
  font-size:clamp(16px, 4.2vw, 30px);
  --pad:clamp(16px, 4.2vw, 34px);
  --fs-label:clamp(12px, 3.2vw, 24px);
  --fs-code:clamp(15px, 4.1vw, 30px);
  --fs-title:clamp(26px, 7.4vw, 56px);
  padding-bottom:calc(env(safe-area-inset-bottom,0px) + 96px);
}
.ct-head{
  position:sticky; top:0; z-index:2;
  padding:calc(env(safe-area-inset-top,0px) + var(--pad)) var(--pad) calc(var(--pad) * .7);
  background:linear-gradient(to bottom,#0D1312 80%,rgba(13,19,18,0));
}
.ct-title{font-size:var(--fs-title); font-weight:600; letter-spacing:-.025em; margin:0 0 .1em; line-height:1.05;}
.ct-sub{margin:0; font-size:var(--fs-label); color:#8A9A94;
  font-family:ui-monospace,SFMono-Regular,Menlo,monospace;
  white-space:nowrap; overflow:hidden; text-overflow:ellipsis;}

/* THE LOCATION, always on screen. See the header. */
.ct-topline{display:flex; align-items:flex-start; gap:12px;}
.ct-topline .ct-titles{flex:1 1 0; min-width:0;}
.ct-signout{
  flex:0 0 auto; background:none; border:1px solid #24352F; border-radius:10px;
  color:#8A9A94; font:inherit; font-size:var(--fs-label);
  padding:8px 12px; cursor:pointer;
}

.ct-loc{
  display:flex; align-items:center; gap:10px; margin-top:14px;
  padding:12px 14px; border-radius:12px;
  background:#152020; border:1px solid #24352F;
}
.ct-loc-label{font-size:var(--fs-label); color:#8A9A94; flex:0 0 auto;}
.ct-loc-name{font-weight:600; flex:1 1 auto; min-width:0;
  white-space:nowrap; overflow:hidden; text-overflow:ellipsis;}
.ct-loc-select{
  flex:1 1 auto; min-width:0; font:inherit; font-weight:600;
  color:#E7EEEB; background:transparent; border:none;
  padding:2px 0; cursor:pointer;
  /* The OS picker draws the list; this only has to look like the row it sits
     in rather than like a form control. */
  appearance:none; -webkit-appearance:none;
}
.ct-loc-select option{background:#101A19; color:#E7EEEB;}

.ct-search{
  display:flex; gap:10px; margin-top:12px;
}
.ct-search input{
  flex:1 1 auto; min-width:0; font:inherit; font-size:var(--fs-code);
  padding:12px 14px; border-radius:12px; color:#E7EEEB;
  background:#101A19; border:1px solid #24352F;
  font-family:ui-monospace,SFMono-Regular,Menlo,monospace;
}
.ct-list{list-style:none; margin:0; padding:0 var(--pad);}
.ct-row{
  display:flex; align-items:center; gap:14px; width:100%;
  padding:16px 0; border-bottom:1px solid #1B2725;
  background:none; border-left:none; border-right:none; border-top:none;
  color:inherit; font:inherit; text-align:left; cursor:pointer;
}
.ct-row-main{flex:1 1 auto; min-width:0;}
/* BLOCK, not inline. These are spans inside a button, and as inline boxes they
   sat on one line — "24000012306Del Monte Canned Sliced Pineapple" — with the
   title's margin-top silently ignored, because margins do not apply to the
   block axis of an inline box. */
.ct-row-code{display:block;
  font-family:ui-monospace,SFMono-Regular,Menlo,monospace;
  font-size:var(--fs-code);}
.ct-row-title{display:block; font-size:var(--fs-label); color:#8A9A94;
  margin-top:4px;
  white-space:nowrap; overflow:hidden; text-overflow:ellipsis;}
.ct-row-qty{flex:0 0 auto; font-size:var(--fs-code); font-weight:600;
  font-variant-numeric:tabular-nums;}
.ct-row-qty.ct-unknown{color:#5C6B66; font-weight:400;}
.ct-row-qty.ct-just-set{color:#6FD5B0;}

.ct-empty{padding:var(--pad); color:#8A9A94;}
.ct-more{display:block; width:calc(100% - var(--pad)*2); margin:16px var(--pad);
  padding:14px; border-radius:12px; background:#152020;
  border:1px solid #24352F; color:#E7EEEB; font:inherit; cursor:pointer;}

/* The confirmation island. Sits just above the scan button, out of the way of
   the thumb that is about to press it. */
.ct-island{
  position:fixed; left:0; right:0;
  bottom:calc(env(safe-area-inset-bottom,0px) + 92px);
  margin:0 var(--pad); padding:12px 16px; border-radius:14px;
  background:#12312A; border:1px solid #2E6B58; color:#BFF0DE;
  display:flex; align-items:center; gap:10px;
  font-size:var(--fs-label); line-height:1.35;
  box-shadow:0 8px 24px rgba(0,0,0,.35);
  animation:ct-island-in .18s ease-out;
}
.ct-island-warn{background:#3A2A12; border-color:#7A5A22; color:#F0D9A8;}
.ct-island-warn .ct-island-tick{color:#F0B37E;}
.ct-island-tick{color:#6FD5B0; font-weight:700; flex:0 0 auto;}
.ct-island-text{min-width:0; overflow:hidden; text-overflow:ellipsis;
  white-space:nowrap;}
@keyframes ct-island-in{
  from{opacity:0; transform:translateY(6px);}
  to{opacity:1; transform:none;}
}

.ct-scan{
  position:fixed; left:0; right:0;
  bottom:calc(env(safe-area-inset-bottom,0px) + 16px);
  margin:0 var(--pad); padding:18px; border-radius:16px; border:none;
  background:#6FD5B0; color:#06110D; font:inherit; font-weight:700;
  font-size:clamp(17px,4.6vw,30px); cursor:pointer;
}

/* --- the popup ---------------------------------------------------------- */
.ct-sheet-back{position:fixed; top:0; left:0; right:0; bottom:0;
  background:rgba(4,9,8,.72); z-index:10;
  display:flex; align-items:flex-end;
}
.ct-sheet{
  width:100%; background:#101A19; border-top-left-radius:20px;
  border-top-right-radius:20px; padding:var(--pad);
  padding-bottom:calc(env(safe-area-inset-bottom,0px) + var(--pad));
  max-height:92dvh; overflow-y:auto;
}
.ct-sheet-code{font-family:ui-monospace,SFMono-Regular,Menlo,monospace;
  font-size:var(--fs-code); color:#8A9A94;}
.ct-sheet-title{font-size:clamp(20px,5.4vw,38px); font-weight:600;
  margin:6px 0 0; line-height:1.15;}
.ct-sheet-prev{margin:18px 0 6px; font-size:var(--fs-label); color:#8A9A94;}
.ct-qty{
  width:100%; box-sizing:border-box; font:inherit;
  /* A DISPLAY, not a field — see Keypad. It carries no focus and raises no
     keyboard, which is the entire point on iOS. */
  font-size:clamp(38px,11vw,80px); font-weight:700; text-align:center;
  font-variant-numeric:tabular-nums; letter-spacing:-.02em;
  padding:18px; border-radius:16px; color:#E7EEEB;
  background:#0A1211; border:1px solid #2C3F38;
}
/* THE NUMBER PAD. Sized for a thumb, not a cursor: 22vw keys on a phone give
   roughly 80px targets, comfortably past the 44px minimum, and the whole pad
   plus the confirm row still fits above the fold on a small screen. */
.ct-pad{
  display:grid; grid-template-columns:repeat(3, 1fr); gap:10px; margin-top:14px;
}
.ct-key{
  font:inherit; font-weight:600; font-size:clamp(22px,6.5vw,34px);
  padding:clamp(14px,3.6vw,22px) 0; border-radius:14px; cursor:pointer;
  color:#E7EEEB; background:#182523; border:1px solid #2C3F38;
  /* No text selection or double-tap zoom while someone taps quickly. */
  user-select:none; -webkit-user-select:none; touch-action:manipulation;
}
.ct-key:active{background:#22322E;}
.ct-key:disabled{opacity:.4; cursor:default;}
.ct-key-alt{background:#141E1D; color:#8A9A94;}
.ct-qty-empty{color:#3E514B;}

.ct-actions{display:flex; gap:12px; margin-top:18px;}
.ct-btn{flex:1 1 0; padding:18px; border-radius:14px; border:none; font:inherit;
  font-weight:700; font-size:clamp(16px,4.4vw,28px); cursor:pointer;}
.ct-btn-primary{background:#6FD5B0; color:#06110D;}
.ct-btn-primary:disabled{opacity:.45; cursor:default;}
.ct-btn-ghost{background:#1B2725; color:#E7EEEB;}
.ct-note{margin-top:14px; font-size:var(--fs-label); line-height:1.45;}
.ct-note-warn{color:#F0B37E;}
.ct-note-bad{color:#F08C8C;}
.ct-note-ok{color:#6FD5B0;}

.ct-picker{list-style:none; margin:14px 0 0; padding:0;}
.ct-picker button{
  width:100%; text-align:left; font:inherit; cursor:pointer;
  padding:16px 0; border-bottom:1px solid #1B2725; color:inherit;
  background:none; border-left:none; border-right:none; border-top:none;
}
.ct-picker .ct-inactive{color:#8A9A94; font-size:var(--fs-label); margin-left:8px;}
`;

/**
 * How a location is written, everywhere.
 *
 * NEVER an id. `read_locations` is granted on only some shops, and where it is
 * missing Shopify will hand back an id and nothing else — but a Shopify GID in
 * front of someone counting a shelf is noise they cannot act on, and it reads
 * as something being broken. Say the honest thing in words instead.
 */
function nameOf(loc) {
  if (!loc) return "no location chosen";
  return loc.name || "Unnamed location";
}

/** Remember the chosen location per handset, so a reload does not lose it. */
const LOCATION_KEY = "cosmos.count.location";

function readStoredLocation() {
  try {
    const raw = window.localStorage.getItem(LOCATION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    // Private browsing or blocked site data. Losing the choice costs one tap;
    // throwing here would cost the screen.
    return null;
  }
}

function storeLocation(loc) {
  try {
    if (loc === null) window.localStorage.removeItem(LOCATION_KEY);
    else window.localStorage.setItem(LOCATION_KEY, JSON.stringify(loc));
  } catch {
    /* best effort */
  }
}

export default function Count({ shop, onExpired }) {
  const [location, setLocation] = useState(readStoredLocation);
  const [locations, setLocations] = useState(null);

  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState(null);

  const [scanning, setScanning] = useState(false);
  const [sheet, setSheet] = useState(null);
  // A short-lived confirmation. Counting is a rhythm — scan, key, confirm, walk
  // — and the popup closing is the only feedback there was, which is the same
  // thing that happens when someone taps Cancel. This says what actually landed.
  const [island, setIsland] = useState(null);

  // Barcodes set during THIS session, so the list can show the number the
  // person just entered rather than the stale mirrored one. Not persisted: it
  // is a display aid for the walk, not a record. The record is the backend log.
  const [justSet, setJustSet] = useState({});

  const expired = useCallback(
    (err) => {
      if (err instanceof SessionExpired) {
        onExpired?.();
        return true;
      }
      return false;
    },
    [onExpired],
  );

  useOverlayHistory(Boolean(sheet), () => setSheet(null));

  // --- locations ----------------------------------------------------------
  const loadLocations = useCallback(async () => {
    try {
      // namesAvailable is deliberately not tracked here any more: nameOf is the
      // one place that decides how a nameless location is written, so nothing
      // on this screen needs to branch on it.
      const { locations: list } = await fetchLocations();
      setLocations(list);
      return list;
    } catch (err) {
      if (expired(err)) return [];
      setListError(messageFor(err));
      return [];
    }
  }, [expired]);

  // LOAD ON MOUNT, ALWAYS — not only when nothing is chosen yet.
  //
  // The earlier version skipped the fetch whenever a location was already
  // stored, which left `locations` null for the whole session. The dropdown
  // then had exactly ONE option in it — the stored one — so tapping it offered
  // no way to switch. Loading them lazily on focus does not help on a phone:
  // the OS picker opens from the tap itself, already showing whatever the list
  // held at that moment.
  //
  // One request per page load, against a shop that has a handful of locations.
  useEffect(() => {
    void (async () => {
      const list = await loadLocations();
      if (list.length === 0) return;

      setLocation((current) => {
        // Nothing chosen: one location is not a choice, so take it. More than
        // one and the dropdown does the asking — with a real list behind it.
        if (!current) {
          if (list.length === 1) {
            storeLocation(list[0]);
            return list[0];
          }
          return current;
        }

        // A STORED CHOICE IS RE-VALIDATED against what Shopify says now. It
        // came out of localStorage and can be months old: the location may have
        // been renamed, or deleted entirely, and counting into an id that no
        // longer exists fails with nothing on screen explaining why.
        const fresh = list.find((l) => l.id === current.id);
        if (!fresh) {
          storeLocation(null);
          return null;
        }
        if (fresh.name !== current.name) {
          storeLocation(fresh);
          return fresh;
        }
        return current;
      });
    })();
    // Once, on mount. `location` is deliberately not a dependency — depending
    // on it would re-fetch every time the person switched location.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadLocations]);

  const chooseLocation = (loc) => {
    setLocation(loc);
    storeLocation(loc);
    // The list's numbers are per-location once the popup reads them, so the
    // in-session overrides no longer describe what is on screen.
    setJustSet({});
  };

  // --- the list -----------------------------------------------------------
  const load = useCallback(
    async ({ q, append = false, from = null }) => {
      setLoading(true);
      setListError(null);
      try {
        const data = await fetchItems({ q, cursor: from });
        setItems((prev) => (append ? [...prev, ...data.items] : data.items));
        setCursor(data.nextCursor || null);
      } catch (err) {
        if (!expired(err)) setListError(messageFor(err));
      } finally {
        setLoading(false);
      }
    },
    [expired],
  );

  // Debounced: this is a LIKE over every barcode the shop has, and a keystroke
  // per query would be one scan of that index per character typed.
  const debounce = useRef(null);
  useEffect(() => {
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => void load({ q: query }), 220);
    return () => clearTimeout(debounce.current);
  }, [query, load]);

  // --- the popup ----------------------------------------------------------
  const openFor = useCallback(
    async (barcode) => {
      // NO LOCATION, NO COUNT — and say why.
      //
      // This used to return silently, so tapping a row or finishing a scan did
      // nothing at all and the screen looked broken. A count needs a location
      // before it can mean anything: the same number written to the wrong shelf
      // has no error and no symptom.
      if (!location) {
        setIsland({ tone: "warn", text: "Choose a location first", at: Date.now() });
        return;
      }
      // Open IMMEDIATELY in a loading state rather than waiting for the read.
      // On a shop floor the round trip is the slowest thing here, and a button
      // that does nothing for a second gets pressed twice.
      setSheet({ barcode, loading: true });
      try {
        const data = await fetchItem({ barcode, locationId: location.id });
        setSheet({
          barcode,
          loading: false,
          found: data.found,
          countable: data.countable !== false,
          reason: data.reason || null,
          title: data.title || null,
          available: data.available,
          measured: Boolean(data.measured),
          // Seed the box with the live figure so the common edit is a small
          // correction, not retyping a number that was already right.
          value:
            data.available === null || data.available === undefined
              ? ""
              : String(data.available),
        });
      } catch (err) {
        if (expired(err)) return;
        // `value: ""` and `unread: true` are both load-bearing.
        //
        // Without a value the input's `value={undefined}` makes React flip it to
        // UNCONTROLLED, and `sheet.value === ""` is false for undefined so the
        // commit button stayed ENABLED — tapping it ran Number(undefined), got
        // NaN, and told the person to "enter a whole number", blaming them for a
        // dropped connection.
        //
        // `unread` says we never got an answer from Shopify, which is different
        // from every other state this sheet has. Nothing may be written against
        // a level we did not read.
        setSheet({
          barcode,
          loading: false,
          unread: true,
          value: "",
          error: messageFor(err),
        });
      }
    },
    [location, expired],
  );

  const commit = useCallback(async ({ create = false } = {}) => {
    if (!sheet || !location) return;
    const qty = Number(sheet.value);
    if (!Number.isInteger(qty) || qty < 0) {
      setSheet((s) => ({ ...s, error: "Enter a whole number, zero or more." }));
      return;
    }

    setSheet((s) => ({ ...s, saving: true, error: null }));
    try {
      const res = await setCount({
        barcode: sheet.barcode,
        locationId: location.id,
        quantity: qty,
        create,
      });
      setJustSet((prev) => ({ ...prev, [sheet.barcode]: res.quantity }));
      setSheet(null);
      setIsland({
        // The title is what the person recognises; the barcode is what they
        // scanned. Show the title when there is one.
        text: res.created
          ? `Added ${res.barcode} and set to ${res.quantity}`
          : `${res.title || res.barcode} set to ${res.quantity}`,
        at: Date.now(),
      });
    } catch (err) {
      if (expired(err)) return;
      // STAYS OPEN on failure, holding the typed number. Closing would lose it
      // and leave the person unsure whether it landed.
      setSheet((s) => ({ ...s, saving: false, error: messageFor(err) }));
    }
  }, [sheet, location, expired]);

  // Clears itself. Deliberately NOT tied to the next action: a counter who sets
  // one item and stands still should still see it disappear on its own, so the
  // screen never carries a stale claim about what was saved.
  useEffect(() => {
    if (!island) return undefined;
    const t = setTimeout(() => setIsland(null), 2600);
    return () => clearTimeout(t);
  }, [island]);

  const onScanned = useCallback(
    (code) => {
      setScanning(false);
      void openFor(code);
    },
    [openFor],
  );

  return (
    <div className="ct-root">
      <style>{CSS}</style>

      <div className="ct-head">
        <div className="ct-topline">
          <div className="ct-titles">
            <h1 className="ct-title">Stock count</h1>
            <p className="ct-sub">{shop}</p>
          </div>
          {/*
            THE WAY OUT. A handset had none: signing out existed in the client
            and was called from nowhere, so a phone in the wrong mode — or on
            the wrong shop — could only be fixed by clearing site data. That is
            not something to ask of someone standing in an aisle.
          */}
          <button
            type="button"
            className="ct-signout"
            onClick={async () => {
              try {
                await leaveSession();
              } catch {
                /* signing out must work even when the session is already dead */
              }
              onExpired?.();
            }}
          >
            Sign out
          </button>
        </div>

        {/*
          A NATIVE <select>, not a custom sheet.

          On a phone this opens the OS picker — a full-height, thumb-sized wheel
          the person already knows how to use — and it costs no layout work of
          ours to get right. The custom overlay it replaces was a second modal
          competing with the count popup for the same screen.

          NAMES ONLY. A raw Shopify location id in front of someone counting a
          shelf is noise they cannot act on; where a name is genuinely missing
          it says so in words instead.
        */}
        <div className="ct-loc">
          <span className="ct-loc-label">Location</span>
          <select
            className="ct-loc-select"
            value={location?.id || ""}
            onChange={(e) => {
              const next = (locations || []).find((l) => l.id === e.target.value);
              if (next) chooseLocation(next);
            }}
            onFocus={() => {
              // A RETRY, not the primary path — they load on mount. This only
              // fires when that load failed (a dropped connection on a shop
              // floor), so focusing the control gives it another go instead of
              // leaving someone with a dropdown that can never populate.
              if (!locations) void loadLocations();
            }}
            aria-label="Location being counted"
          >
            {!location ? <option value="">Choose a location…</option> : null}
            {(locations || (location ? [location] : [])).map((l) => (
              <option key={l.id} value={l.id}>
                {nameOf(l)}
              </option>
            ))}
          </select>
        </div>


        <div className="ct-search">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search barcode"
            inputMode="numeric"
            autoComplete="off"
            aria-label="Search by barcode"
          />
        </div>
      </div>

      {listError ? <p className="ct-empty">{listError}</p> : null}

      <ul className="ct-list">
        {items.map((it) => {
          // AT THIS LOCATION, OR NOTHING.
          //
          // This used to fall back to the list's `lastKnownQty`, which was
          // `Variant.inventory` — the ROLLUP across EVERY location. Beside a
          // count scoped to one shelf that is not a stale number, it is a
          // DIFFERENT quantity, and it invited counting against stock held
          // somewhere else entirely.
          //
          // The only per-location figure this screen can have for free is one it
          // set itself. Everything else reads as "not counted here", and the
          // popup fetches the real number for the chosen location on open.
          const qty = justSet[it.barcode];
          return (
            <li key={it.variantId}>
              <button
                type="button"
                className="ct-row"
                onClick={() => void openFor(it.barcode)}
              >
                <span className="ct-row-main">
                  <span className="ct-row-code">{it.barcode}</span>
                  {it.title ? <span className="ct-row-title">{it.title}</span> : null}
                </span>
                <span
                  className={
                    "ct-row-qty" + (qty === undefined ? " ct-unknown" : " ct-just-set")
                  }
                  title={
                    qty === undefined
                      ? "Not counted at this location yet"
                      : "Counted here in this session"
                  }
                >
                  {/* An em dash, not a zero. A zero here is a number someone
                      would count against; "not counted at this location" is the
                      truth, and the two must never look alike. */}
                  {qty === undefined ? "—" : qty}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      {!loading && items.length === 0 && !listError ? (
        <p className="ct-empty">
          {query ? "No barcode matches that." : "No barcodes in this store yet."}
        </p>
      ) : null}

      {cursor ? (
        <button
          type="button"
          className="ct-more"
          onClick={() => void load({ q: query, append: true, from: cursor })}
          disabled={loading}
        >
          {loading ? "Loading…" : "Show more"}
        </button>
      ) : null}

      {island ? (
        <div
          className={"ct-island" + (island.tone === "warn" ? " ct-island-warn" : "")}
          role="status"
          aria-live="polite"
        >
          <span className="ct-island-tick" aria-hidden="true">
            {island.tone === "warn" ? "!" : "✓"}
          </span>
          <span className="ct-island-text">{island.text}</span>
        </div>
      ) : null}

      <button
        type="button"
        className="ct-scan"
        onClick={() => {
          // Refuse BEFORE the camera opens rather than after the scan. Reading a
          // barcode and then being told it cannot be used wastes the one action
          // that costs the person real effort.
          if (!location) {
            setIsland({ tone: "warn", text: "Choose a location first", at: Date.now() });
            return;
          }
          setScanning(true);
        }}
      >
        Scan a barcode
      </button>

      <BarcodeScanModal
        open={scanning}
        onResult={onScanned}
        onClose={() => setScanning(false)}
      />

      {sheet ? (
        <CountSheet
          sheet={sheet}
          location={location}
          onChange={(v) => setSheet((s) => ({ ...s, value: v }))}
          onCommit={commit}
          onClose={() => setSheet(null)}
        />
      ) : null}
    </div>
  );
}


/**
 * The number pad.
 *
 * ---------------------------------------------------------------------------
 * WHY NOT THE DEVICE KEYBOARD
 * ---------------------------------------------------------------------------
 * iOS Safari raises the keyboard ONLY when focus() happens inside a real user
 * gesture. After a scan there is no gesture left: the barcode arrives from a
 * video frame, a fetch follows it, and the effect that focuses the field runs
 * long after the tap that started the camera. Safari ignores it. So on an
 * iPhone the sheet opened with a field nobody could type into until they
 * tapped it, and on Android it worked — the same code, two behaviours.
 *
 * Rather than chase a gesture that is genuinely gone, the sheet carries its own
 * digits. That is not a workaround dressed as a feature; for this job it is the
 * better control:
 *
 *   - identical on every device, with no focus rules to satisfy
 *   - no OS keyboard sliding over the sheet, so the number and the confirm
 *     button are both visible the whole time
 *   - thumb-sized targets for someone holding a phone in one hand and a
 *     product in the other
 *
 * A hardware keyboard still works — see the key handler in CountSheet — so the
 * lab and a desktop browser are unaffected.
 */
export function Keypad({ value, onChange, disabled }) {
  const press = (d) => {
    if (disabled) return;
    // Leading zeros are meaningless for a count and read as a typo.
    const next = (value === "0" ? "" : value) + d;
    // Four digits is 9,999 of one item on one shelf. Beyond that it is a
    // mis-tap, and a silently accepted 55,555 becomes a real inventory number.
    if (next.length > 4) return;
    onChange(next);
  };

  return (
    <div className="ct-pad" role="group" aria-label="Number pad">
      {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
        <button key={d} type="button" className="ct-key" onClick={() => press(d)} disabled={disabled}>
          {d}
        </button>
      ))}
      <button
        type="button"
        className="ct-key ct-key-alt"
        onClick={() => !disabled && onChange("")}
        disabled={disabled}
        aria-label="Clear"
      >
        C
      </button>
      <button type="button" className="ct-key" onClick={() => press("0")} disabled={disabled}>
        0
      </button>
      <button
        type="button"
        className="ct-key ct-key-alt"
        onClick={() => !disabled && onChange(value.slice(0, -1))}
        disabled={disabled}
        aria-label="Backspace"
      >
        &#9003;
      </button>
    </div>
  );
}

/** One barcode: what Shopify holds now, and what the counter says it is. */
function CountSheet({ sheet, location, onChange, onCommit, onClose }) {
  // An unnamed location must not render as "at undefined" in a sentence that
  // tells someone where their number is going.
  const where = location ? nameOf(location) : "this location";

  // A HARDWARE KEYBOARD STILL WORKS. The pad is what a phone uses; a desktop or
  // a bluetooth scanner-keyboard should not be made worse for it. Bound to the
  // document because the sheet holds no focusable field any more.
  useEffect(() => {
    const onKey = (e) => {
      if (sheet.saving) return;
      if (e.key >= "0" && e.key <= "9") {
        const next = (sheet.value === "0" ? "" : sheet.value) + e.key;
        if (next.length <= 4) onChange(next);
      } else if (e.key === "Backspace") {
        onChange(sheet.value.slice(0, -1));
      } else if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sheet.value, sheet.saving, onChange, onClose]);

  // `found === false` is NOT blocked: an unknown barcode is countable by
  // creating it. A product Shopify will not stock is a dead end, and so is a
  // sheet whose read never returned — writing against a level we did not read
  // is the one thing this screen must never do.
  const blocked =
    sheet.loading || sheet.saving || sheet.unread === true || sheet.countable === false;

  return (
    <div
      className="ct-sheet-back"
      role="dialog"
      aria-modal="true"
    >
      <div className="ct-sheet">
        <p className="ct-sheet-code">{sheet.barcode}</p>
        {sheet.title ? <h2 className="ct-sheet-title">{sheet.title}</h2> : null}

        {sheet.loading ? (
          <p className="ct-note">Reading Shopify…</p>
        ) : sheet.unread ? (
          // NO CLAIM ABOUT SHOPIFY. The previous version fell through to the
          // final branch and printed "No count recorded at <location> yet"
          // beside the error — a statement about a level that was never read.
          <p className="ct-note ct-note-bad">
            Could not read this item. Nothing has been changed — close and try
            again.
          </p>
        ) : sheet.found === false ? (
          <>
            <p className="ct-note ct-note-warn">
              Not in the catalogue yet. Setting a count adds it to Shopify as a
              DRAFT product, ready for photographs later.
            </p>
            <div className="ct-qty" role="status" aria-live="polite" aria-label="Counted quantity">
              {sheet.value === "" ? <span className="ct-qty-empty">0</span> : sheet.value}
            </div>
            <Keypad value={sheet.value} onChange={onChange} disabled={sheet.saving} />
          </>
        ) : sheet.countable === false ? (
          <p className="ct-note ct-note-warn">
            Shopify is not tracking stock for this product, so there is nothing to set.
          </p>
        ) : (
          <>
            <p className="ct-sheet-prev">
              {sheet.available === null
                ? `Not stocked at ${where} yet — confirming will start stocking it here.`
                : sheet.measured
                  ? `Shopify has ${sheet.available} at ${where}`
                  : `No count recorded at ${where} yet`}
            </p>
            <div className="ct-qty" role="status" aria-live="polite" aria-label="Counted quantity">
              {sheet.value === "" ? <span className="ct-qty-empty">0</span> : sheet.value}
            </div>
            <Keypad value={sheet.value} onChange={onChange} disabled={sheet.saving} />
          </>
        )}

        {sheet.error ? <p className="ct-note ct-note-bad">{sheet.error}</p> : null}

        <div className="ct-actions">
          <button type="button" className="ct-btn ct-btn-ghost" onClick={onClose}>
            {blocked ? "Close" : "Cancel"}
          </button>
          {sheet.unread ? null : sheet.found === false ? (
            // A SEPARATE BUTTON, worded as the thing it actually does. This
            // creates a product in the merchant's catalogue, and that must
            // never be something someone discovers afterwards.
            <button
              type="button"
              className="ct-btn ct-btn-primary"
              onClick={() => onCommit({ create: true })}
              disabled={sheet.saving || sheet.value === ""}
            >
              {sheet.saving ? "Adding to Shopify…" : "Add and set count"}
            </button>
          ) : !blocked || sheet.saving ? (
            <button
              type="button"
              className="ct-btn ct-btn-primary"
              onClick={() => onCommit()}
              disabled={sheet.saving || sheet.value === ""}
            >
              {/* Says where it goes. "Save" would understate it: this is the
                  merchant's live storefront, not a draft. */}
              {sheet.saving ? "Updating Shopify…" : "Update Shopify"}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
